// El teléfono de la venta sigue apartado; sin él el silo rechaza la forma de pago (SALE_PHONE_REQUIRED).
import { ApiError } from '@/api/errors';
import { anchorLineOf, extraLinesOf } from './buildSale';
import { sessionHasPhone } from './productSuggestions';
import { isReservationLive } from './reservationClock';
import type { ReservedLine, WizardSession } from './types';

export const PHONE_EXPIRED_MESSAGE = 'Se venció el apartado del teléfono. Elige uno para seguir.';

/** Con estos minutos o menos, el reloj avisa que el teléfono está por vencer. */
export const PHONE_WARNING_MINUTES = 5;

/** La venta tiene teléfono (o kit con teléfono) y su apartado no ha vencido. */
export function hasLivePhone(session: WizardSession, now: number): boolean {
    const anchor = anchorLineOf(session);
    return (
        sessionHasPhone(session) && anchor !== undefined && isReservationLive(anchor.expiresAt, now)
    );
}

/** Igual que `hasLivePhone`, con la hora de este momento; para handlers, no para render. */
export function hasLivePhoneNow(session: WizardSession): boolean {
    return hasLivePhone(session, Date.now());
}

/** El teléfono sigue en la venta pero su apartado ya venció. */
export function hasExpiredPhone(session: WizardSession, now: number): boolean {
    const anchor = anchorLineOf(session);
    return anchor !== undefined && !isReservationLive(anchor.expiresAt, now);
}

/** Aviso de un accesorio cuyo apartado venció. */
export function extraExpiredMessage(name: string): string {
    return `Se venció el apartado de ${name}. Agrégalo de nuevo si lo quieres.`;
}

/** Extras de la venta (ni el teléfono ni sus regalos) cuyo apartado ya venció. */
export function expiredExtraLinesOf(session: WizardSession, now: number): ReservedLine[] {
    return extraLinesOf(session).filter((l) => !isReservationLive(l.expiresAt, now));
}

/** Error del silo que significa que la venta perdió lo apartado o el producto que la sostiene. */
function isLostReservationCode(code: string): boolean {
    return code === 'SALE_PHONE_REQUIRED' || code === 'RESERVATION_EXPIRED';
}

/**
 * Qué se perdió según el error del silo: el teléfono, un extra vencido o nada que ver.
 * Son los extras solo si el extra venció y el teléfono vive.
 */
export function lostReservationOf(
    error: unknown,
    session: WizardSession | undefined,
    now: number,
): 'phone' | 'extra' | undefined {
    if (!(error instanceof ApiError) || !isLostReservationCode(error.code)) return undefined;
    if (error.code === 'SALE_PHONE_REQUIRED') return 'phone';
    const extraExpired =
        session !== undefined &&
        hasLivePhone(session, now) &&
        expiredExtraLinesOf(session, now).length > 0;
    return extraExpired ? 'extra' : 'phone';
}
